import React from 'react';
import styled from 'styled-components';
import {
  screenMdMin,
  screenXlMin,
  spacer24,
  spacer40,
  spacer48,
} from '@spotify-internal/encore-foundation';
import { Card } from './Card';
import chartsCardImage from './assets/home-img-charts@2x.png';
import promoCardImage from './assets/promo-card-home-card.png';
import theInputCardImage from './assets/the-input-2020.png';
import homeCardImage2 from './assets/home-card-02.png';
import homeCardImage3 from './assets/home-card-03.png';
import { useViewport, Viewport } from '../../../hooks/useViewport';
import { useMe } from '../../../hooks/useMe';
import { isS4PodHomeCardPlaylistEnabled } from '../../../lib/Features';
import { PlaylistAddsCard } from './PlaylistAddsCard';
import { useHomeTracker } from '../../../lib/Tracking/HomeTracker';
import { getHomeCards } from '../../../lib/Api/homeCards';
import { ChartAddsCard } from './ChartAddsCard';

const BLOG_URL = 'https://podcasters.spotify.com/blog';

type Post = {
  imageUrl: string;
  url: string;
  title: string;
  description: string;
  customCTA?: string;
};

export const posts: Post[] = [
  {
    imageUrl: promoCardImage,
    url: 'http://promocards.byspotify.com/podcasters',
    title: ' Promo Cards: Shareable social media assets for podcasters',
    description:
      'Create and customize your own graphics to share with fans and promote your podcast',
    customCTA: 'Check it out',
  },
  {
    imageUrl: chartsCardImage,
    url: BLOG_URL + '/get-discovered-on-spotifys-new-podcast-charts',
    title: "Get discovered on Spotify's new podcast charts",
    description:
      "Learn about Spotify's new podcast charts, as well as the new notifications and share cards within Spotify for Podcasters",
  },
  {
    imageUrl: theInputCardImage,
    url: BLOG_URL + '/introducing-the-input-a-new-video-series-from-spotify-for-podcasters',
    title: 'Check out The Input, a new video series from Spotify for Podcasters.',
    description: 'Get advice from Spotify podcasting experts about our tools from the inside out. ',
    customCTA: 'Watch now',
  },
  {
    imageUrl: homeCardImage2,
    url: BLOG_URL + '/gimlet-academy-has-arrived',
    title: 'Spotify for Podcasters presents Gimlet Academy',
    description:
      'Take your show to the next level with expert lessons from podcast pioneer Alex Blumberg.',
  },
  {
    imageUrl: homeCardImage3,
    url: BLOG_URL + '/a-quick-guide-to-spotifys-podcast-metrics',
    title: 'A quick guide to Spotify’s podcast metrics',
    description: 'Plus what to do with all that data.',
  },
];

export function getNumOfColumns() {
  switch (useViewport()) {
    case Viewport.MD:
    case Viewport.LG:
      return 2;
    case Viewport.XL:
      return 3;
    default:
      return 1;
  }
}

export function getCardColumns(numColumns: number, cards: JSX.Element[]) {
  let columns: JSX.Element[][] = [];

  for (let i = 0; i < numColumns; i++) {
    columns.push(cards.filter((_, index) => index % numColumns === i));
  }
  return columns;
}

type Props = {
  networkId: string | null;
};

export function CardsSection({ networkId }: Props) {
  const numColumns = getNumOfColumns();
  const me = useMe();

  const [homeCards, setHomeCards] = React.useState<JSX.Element[]>(
    posts.map(({ url, imageUrl, title, description, customCTA }) => (
      <Card
        key={url}
        imageUrl={imageUrl}
        href={url}
        title={title}
        description={description}
        callToAction={{ external: true, text: customCTA || 'Read More' }}
      />
    )),
  );

  React.useEffect(() => {
    if (me && isS4PodHomeCardPlaylistEnabled(me.features)) {
      getHomeCards(networkId).then(({ chartAdds, playlistAdds }) => {
        let addsCards: JSX.Element[] = [];
        if (chartAdds) {
          addsCards.push(
            <ChartAddsCard
              key={chartAdds.imageUrl}
              imageUrl={chartAdds.imageUrl}
              chartAdds={chartAdds.chartAdds}
              networkId={networkId}
              tracker={homeTracker}
            />,
          );
        }
        if (playlistAdds) {
          addsCards.push(
            <PlaylistAddsCard
              key={playlistAdds.imageUrl}
              networkId={networkId}
              imageUrl={playlistAdds.imageUrl}
              playlistAdds={playlistAdds.playlistAdds}
              tracker={homeTracker}
            />,
          );
        }
        setHomeCards(cards => [...addsCards, ...cards]);
      });
    }
  }, [networkId, me]);

  const homeTracker = useHomeTracker();

  // map over a 2-D array of posts per column so that each column can be wrapped by a <div>
  return (
    <CardsGrid>
      {getCardColumns(numColumns, homeCards).map((column, i) => (
        <div key={i}>{column.map(card => card)}</div>
      ))}
    </CardsGrid>
  );
}

const CardsGrid = styled.div`
  grid-area: cards;

  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${spacer24};
  margin-top: ${spacer48};

  @media (min-width: ${screenMdMin}) {
    grid-gap: ${spacer40};
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${screenXlMin}) {
    grid-gap: ${spacer40};
    grid-template-columns: repeat(3, 1fr);
  }
`;
